import { DashboardMap } from "@/components/dashboard/dashboard-map";
import { DashboardMapViewLayout } from "@/components/dashboard/dashboard-map-view-layout";
import { DashboardViewToggle } from "@/components/dashboard/dashboard-view-toggle";
import { LicenseDetailsSheet } from "@/components/dashboard/license-details-sheet";
import { MapErrorBoundary } from "@/components/dashboard/map-error-boundary";
import { RegistrantInspectionDispatchPage } from "@/components/inspection/dispatch-page";
import { DashboardColumnFilterBadges } from "@/components/non_registrant/dashboard-column-filter-badges";
import {
  type ColumnFilterBadge,
  buildColumnFilterBadges,
  buildRegistrantColumnFilterBadges,
  removeColumnFilterBadge,
} from "@/components/non_registrant/dashboard-column-filter-utils";
import { DashboardMapFacetRail } from "@/components/non_registrant/dashboard-map-facet-rail";
import {
  type ExportColumnMode,
  exportTableRowsToCsv,
  exportTableRowsToExcel,
  getExportAccessorKeysForMode,
} from "@/components/non_registrant/dashboard-table";
import { DashboardHeader } from "@/components/registrant/dashboard/dashboard-header";
import { DashboardInvoices } from "@/components/registrant/dashboard/dashboard-invoices";
import { DashboardList } from "@/components/registrant/dashboard/dashboard-list";
import { DashboardPayments } from "@/components/registrant/dashboard/dashboard-payments";
import { DashboardRegistrantUnregistered } from "@/components/registrant/dashboard/dashboard-registrant-unregistered";
import { DashboardTabList } from "@/components/registrant/dashboard/dashboard-tab-list";
import { DashboardUsers } from "@/components/registrant/dashboard/dashboard-users";
import { EmptyDashboard } from "@/components/registrant/dashboard/empty-dashboard";
import {
  REGISTRANT_LICENSE_COLUMN_KEYS,
  buildRegistrantCardMapColumnProperties,
  computeRegistrantLicenseFacetedValues,
  getRegistrantLicenseColumnDisplayName,
  registrantQuestionColumnAccessorKey,
} from "@/components/registrant/dashboard/license-list/license-table";
import {
  REGISTRANT_BULK_LICENSE_THRESHOLD,
  buildRegistrantBulkAccessorKeys,
  buildRegistrantBulkGetColumnProperties,
} from "@/components/registrant/dashboard/registrant-bulk-registrations-table";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ErrorComponent from "@/components/ui/error";
import LoadingScreen from "@/components/ui/loading-screen";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { UserType } from "@/constants";
import {
  getLicenseCommentsOptions,
  getLicensePaymentsOptions,
  getLicenseTypeQuestionsOptions,
  getLicenseTypesOptions,
  getRegistrantAccountUsersOptions,
  getRegistrantLicenseFilterValuesOptions,
  getRegistrantLicenseJurisdictionOptionsOptions,
  getRegistrantLicenseStatsOptions,
  getRegistrantLicensesOptions,
  getRegistrantUnregisteredPropertiesOptions,
} from "@/hera-api/client/@tanstack/react-query.gen";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import { useLicenseDetailsWithRuntimeRefresh } from "@/hooks/use-license-details-with-runtime-refresh";
import {
  type RegistrantLicenseListTab,
  fetchRegistrantLicenseExport,
  fetchRegistrantLicenseFilterValues,
  usePaginatedRegistrantLicenses,
  useRegistrantLicenseMapPins,
} from "@/hooks/use-paginated-registrant-licenses";
import { useDashboardData } from "@/hooks/use-registrant-dashboard-data";
import { useRegistrantDashboardFilters } from "@/hooks/use-registrant-dashboard-filters";
import { useRegistrantOrgPermissions } from "@/hooks/use-registrant-org-permissions";
import { useSyncGlobalColumnFilters } from "@/hooks/use-sync-global-column-filters";
import { jurisdictionOptionLabel } from "@/lib/jurisdiction-option-label";
import { refreshLicensesAfterLocalChange } from "@/lib/license-details-change-notification";
import { setLicenseDisplayedSnapshot } from "@/lib/license-displayed-snapshot";
import { listableRegistrantQuestionColumns } from "@/lib/listable-answer-types";
import type { MapViewport } from "@/lib/map-clusters";
import { findAmsQuestionColumn } from "@/lib/registrant-ams-utils";
import { getDashboardContextHelpArticleId } from "@/lib/registrant-help";
import { omitGloballyLinkedColumnFilters } from "@/lib/sync-global-column-filters";
import { cn, getQueryErrorHeading, getQueryErrorMessage } from "@/lib/utils";
import { SESSION_KEYS, sessionStorage } from "@/types/session-storage";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { SortingState } from "@tanstack/react-table";
import { ChevronDown, Columns3, Loader2, RefreshCw } from "lucide-react";
import * as React from "react";

export const Route = createFileRoute("/registrant/dashboard")({
  component: RegistrantDashboard,
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    openLicense?: string;
    focusProperty?: boolean;
    tab?: string;
  } => ({
    openLicense:
      typeof search.openLicense === "string" ? search.openLicense : undefined,
    focusProperty:
      search.focusProperty === true || search.focusProperty === "true"
        ? true
        : undefined,
    tab: typeof search.tab === "string" ? search.tab : undefined,
  }),
});

const LICENSE_WORKSPACE_TABS = new Set([
  "all",
  "compliance-issues",
  "needs-attention",
  "balance-due",
  "renewable",
  "inactive",
]);

export function RegistrantDashboard() {
  const { isAuthSetupComplete } = useAuthQuerySetup();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    selectedJurisdictions,
    setSelectedJurisdictions,
    selectedLicenseTypes,
    setSelectedLicenseTypes,
    isMapView,
    setIsMapView,
    selectedTab,
    setSelectedTab,
    columnVisibility,
    setColumnVisibility,
    columnFilters,
    setColumnFilters,
    bulkColumnFilters,
    setBulkColumnFilters,
    bulkColumnVisibility,
    setBulkColumnVisibility,
    selectedRegistrantAccountId,
    setSelectedRegistrantAccountId,
    setBalanceDueCohortFilterActive,
    bulkPaymentPayLaterCohortFilterActive,
    setBulkPaymentPayLaterCohortFilterActive,
    selectedAms,
    setSelectedAms,
  } = useRegistrantDashboardFilters();

  const { isAdmin, account: registrantOrgAccount } =
    useRegistrantOrgPermissions();

  React.useEffect(() => {
    if (registrantOrgAccount && selectedTab === "users" && !isAdmin) {
      setSelectedTab("all");
    }
  }, [isAdmin, registrantOrgAccount, selectedTab, setSelectedTab]);

  const [selectedLicenseId, setSelectedLicenseId] = React.useState<
    string | null
  >(null);
  const [isSheetOpen, setIsSheetOpen] = React.useState(false);
  const [licenseDetailsInitialFocus, setLicenseDetailsInitialFocus] =
    React.useState<"property" | null>(null);
  const [pageIndex, setPageIndex] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(50);
  const [sorting, setSorting] = React.useState<SortingState>([
    { id: "last_updated_at", desc: true },
  ]);
  const [unregisteredPageIndex, setUnregisteredPageIndex] = React.useState(0);
  const [unregisteredPageSize, setUnregisteredPageSize] = React.useState(50);
  const [unregisteredSorting, setUnregisteredSorting] =
    React.useState<SortingState>([{ id: "last_updated_at", desc: true }]);
  const [unregisteredColumnFilters, setUnregisteredColumnFilters] =
    React.useState<Record<string, string[]>>({});
  const [unregisteredColumnVisibility, setUnregisteredColumnVisibility] =
    React.useState<Record<string, boolean>>({});
  const [facetValuesByColumn, setFacetValuesByColumn] = React.useState<
    Record<string, string[]>
  >({});
  const [amsQuestionId, setAmsQuestionId] = React.useState<string | null>(null);
  const [isListRefreshDialogOpen, setIsListRefreshDialogOpen] =
    React.useState(false);
  const [maxUnfilteredListTotal, setMaxUnfilteredListTotal] = React.useState(0);

  const listTab: RegistrantLicenseListTab | undefined =
    selectedTab === "needs-attention"
      ? "needs_attention"
      : selectedTab === "compliance-issues"
        ? "compliance_issues"
        : selectedTab === "balance-due"
          ? "balance_due"
          : selectedTab === "renewable"
            ? "renewable"
            : selectedTab === "inactive"
              ? "inactive"
              : LICENSE_WORKSPACE_TABS.has(selectedTab)
                ? "all_active"
                : undefined;

  const { data: statsData } = useQuery({
    ...getRegistrantLicenseStatsOptions({
      query: {
        license_type_ids:
          selectedLicenseTypes.length > 0 ? selectedLicenseTypes : undefined,
        registrant_account_id: selectedRegistrantAccountId || undefined,
        ams_question_id: amsQuestionId || undefined,
        ams_company_name: selectedAms || undefined,
      },
    }),
    enabled: isAuthSetupComplete,
    placeholderData: keepPreviousData,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset when list scope changes, not column filters
  React.useEffect(() => {
    setMaxUnfilteredListTotal(0);
  }, [
    selectedTab,
    selectedJurisdictions,
    selectedLicenseTypes,
    selectedRegistrantAccountId,
    selectedAms,
    bulkPaymentPayLaterCohortFilterActive,
  ]);

  // Derived before the list query so the rendered layout, the filter record the
  // query sends, and the filter chips all agree. When these disagreed, the bulk
  // table wrote to bulkColumnFilters while the query kept reading columnFilters,
  // so filtering and clearing silently did nothing.
  const bulkLayoutTotalCount = Math.max(
    statsData?.total_registrations ?? 0,
    maxUnfilteredListTotal,
  );

  const registrantBulkMode =
    LICENSE_WORKSPACE_TABS.has(selectedTab) &&
    bulkLayoutTotalCount > REGISTRANT_BULK_LICENSE_THRESHOLD;

  const listColumnFilters = registrantBulkMode
    ? bulkColumnFilters
    : columnFilters;

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset to first page when list filters change
  React.useEffect(() => {
    setPageIndex(0);
  }, [
    selectedJurisdictions,
    selectedLicenseTypes,
    selectedTab,
    selectedRegistrantAccountId,
    selectedAms,
    bulkPaymentPayLaterCohortFilterActive,
    listColumnFilters,
    sorting,
  ]);

  const sortBy = sorting[0]?.id ?? "last_updated_at";
  const sortDir = sorting[0]?.desc === false ? "asc" : "desc";

  const { data: licenseTypesData } = useQuery({
    ...getLicenseTypesOptions(),
    enabled: isAuthSetupComplete,
  });

  const {
    licenses,
    totalCount,
    hasMultipleRegistrantAccounts: has_multiple_registrant_accounts,
    questionColumns: rawQuestionColumns,
    isLoading: isQueryLoading,
    isFetching: isListFetching,
    isForegroundFetching: isListForegroundFetching,
    error: queryError,
    dataUpdatedAt,
  } = usePaginatedRegistrantLicenses({
    jurisdictionDisplayCodes: selectedJurisdictions,
    licenseTypeIds: selectedLicenseTypes,
    page: pageIndex,
    pageSize,
    sortBy,
    sortDir,
    columnFilters: listColumnFilters,
    tab: listTab ?? "all_active",
    bulkPaymentCohort: bulkPaymentPayLaterCohortFilterActive,
    registrantAccountId: selectedRegistrantAccountId,
    amsQuestionId: selectedAms ? amsQuestionId : undefined,
    amsCompanyName: selectedAms,
    enabled: isAuthSetupComplete,
  });

  const amsColumn = React.useMemo(
    () => findAmsQuestionColumn(rawQuestionColumns),
    [rawQuestionColumns],
  );

  React.useEffect(() => {
    if (!amsColumn) return;
    setAmsQuestionId(amsColumn.question_id);
    // The bulk payment screen filters on the same question so it shows the same
    // properties, even when an account has more than one AMS-style question.
    sessionStorage.set(
      SESSION_KEYS.REGISTRANT_DASHBOARD_AMS_QUESTION_ID,
      amsColumn.question_id,
    );
  }, [amsColumn]);

  React.useEffect(() => {
    setMaxUnfilteredListTotal((current) => Math.max(current, totalCount));
  }, [totalCount]);

  const { data: universeStats } = useQuery({
    ...getRegistrantLicenseStatsOptions(),
    enabled: isAuthSetupComplete,
  });

  const { data: inactiveProbe } = useQuery({
    ...getRegistrantLicensesOptions({
      query: { tab: "inactive", page: 0, page_size: 1 },
    }),
    enabled:
      isAuthSetupComplete && (universeStats?.total_registrations ?? 0) === 0,
  });

  const { data: registrantUsersData } = useQuery({
    ...getRegistrantAccountUsersOptions(),
    enabled: isAuthSetupComplete && has_multiple_registrant_accounts,
  });

  const { data: jurisdictionOptionsData } = useQuery({
    ...getRegistrantLicenseJurisdictionOptionsOptions(),
    enabled: isAuthSetupComplete,
  });

  const { data: licenseTypeNameValues } = useQuery({
    ...getRegistrantLicenseFilterValuesOptions({
      query: { column: "license_type.name" },
    }),
    enabled: isAuthSetupComplete,
  });

  const { data: amsFilterValues } = useQuery({
    ...getRegistrantLicenseFilterValuesOptions({
      query: {
        column: registrantQuestionColumnAccessorKey(amsColumn?.key ?? ""),
      },
    }),
    enabled: isAuthSetupComplete && !!amsColumn,
  });

  const {
    data: registrantUnregisteredData,
    isLoading: isRegistrantUnregisteredLoading,
    isFetching: isRegistrantUnregisteredFetching,
    error: registrantUnregisteredError,
  } = useQuery({
    ...getRegistrantUnregisteredPropertiesOptions({
      query: {
        jurisdiction_display_codes:
          selectedJurisdictions.length > 0 ? selectedJurisdictions : undefined,
        license_type_ids:
          selectedLicenseTypes.length > 0 ? selectedLicenseTypes : undefined,
        registrant_account_id: selectedRegistrantAccountId || undefined,
        page: unregisteredPageIndex,
        page_size: unregisteredPageSize,
        sort_by: unregisteredSorting[0]?.id ?? "last_updated_at",
        sort_dir: unregisteredSorting[0]?.desc === false ? "asc" : "desc",
        ams_company_name: selectedAms || undefined,
        filters:
          Object.keys(unregisteredColumnFilters).length > 0
            ? JSON.stringify(unregisteredColumnFilters)
            : undefined,
      },
    }),
    enabled: isAuthSetupComplete,
    placeholderData: keepPreviousData,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset paging when list scope changes
  React.useEffect(() => {
    setUnregisteredPageIndex(0);
  }, [
    selectedJurisdictions,
    selectedLicenseTypes,
    selectedRegistrantAccountId,
    selectedAms,
    unregisteredColumnFilters,
    unregisteredSorting,
  ]);

  const {
    licenseData: selectedLicenseData,
    isPreviewLoading: isSelectedLicenseLoading,
    isRuntimeRefreshing: isSelectedLicenseRuntimeRefreshing,
    previewError: selectedLicenseError,
  } = useLicenseDetailsWithRuntimeRefresh({
    licenseId: selectedLicenseId,
    enabled: isAuthSetupComplete && !!selectedLicenseId,
  });

  const {
    data: additionalQuestions,
    isLoading: isAdditionalQuestionsQueryLoading,
    error: additionalQuestionsQueryError,
  } = useQuery({
    ...getLicenseTypeQuestionsOptions({
      path: {
        license_type_id: selectedLicenseData?.license_type.id as string,
        jurisdiction_id: selectedLicenseData?.jurisdiction.id as string,
      },
    }),
    enabled:
      isAuthSetupComplete && !isSelectedLicenseLoading && !!selectedLicenseData,
  });

  const {
    data: commentsData,
    isLoading: isCommentsQueryLoading,
    error: commentsQueryError,
  } = useQuery({
    ...getLicenseCommentsOptions({
      path: {
        license_id: selectedLicenseId as string,
      },
    }),
    enabled: isAuthSetupComplete && !!selectedLicenseId,
  });

  const {
    data: paymentsData,
    isLoading: isPaymentsQueryLoading,
    error: paymentsQueryError,
  } = useQuery({
    ...getLicensePaymentsOptions({
      path: {
        license_id: selectedLicenseId as string,
      },
    }),
    enabled: isAuthSetupComplete && !!selectedLicenseId,
  });

  const grouped = useDashboardData({
    data: {
      licenses,
      has_multiple_registrant_accounts,
    },
    selectedJurisdictions: [],
    selectedLicenseTypes: [],
    selectedRegistrantAccountId: null,
    selectedAms: null,
    amsQuestionId: null,
  });

  const allActiveRegistrationsJurisdictions =
    selectedTab === "all" ? grouped.allActiveRegistrationsJurisdictions : [];
  const needsAttentionLicenses =
    selectedTab === "needs-attention" ? grouped.needsAttentionLicenses : [];
  const complianceIssueLicenses =
    selectedTab === "compliance-issues" ? grouped.complianceIssueLicenses : [];
  const balanceDueLicenses =
    selectedTab === "balance-due" ? grouped.balanceDueLicenses : [];
  const renewableLicenses =
    selectedTab === "renewable" ? grouped.renewableLicenses : [];
  const inactiveLicenses =
    selectedTab === "inactive" ? grouped.inactiveLicenses : [];

  const [mapViewport, setMapViewport] = React.useState<MapViewport | null>(
    null,
  );
  const handleMapViewport = React.useCallback((viewport: MapViewport) => {
    setMapViewport(viewport);
  }, []);
  const {
    pins,
    clusters: mapClusters,
    totalInView: mapTotalInView,
    isFetching: isPinsFetching,
    isForegroundFetching: isPinsForegroundFetching,
    cancel: cancelPinsLoad,
  } = useRegistrantLicenseMapPins({
    jurisdictionDisplayCodes: selectedJurisdictions,
    licenseTypeIds: selectedLicenseTypes,
    columnFilters: listColumnFilters,
    tab: listTab,
    bulkPaymentCohort: bulkPaymentPayLaterCohortFilterActive,
    registrantAccountId: selectedRegistrantAccountId,
    amsQuestionId: selectedAms ? amsQuestionId : undefined,
    amsCompanyName: selectedAms,
    enabled: isAuthSetupComplete && isMapView && listTab != null,
    viewport: mapViewport,
  });

  const amsOptions = React.useMemo(
    () =>
      (amsFilterValues?.values ?? []).map((name) => ({
        value: name,
        label: name,
      })),
    [amsFilterValues?.values],
  );

  // Values stay display codes because that is what the list filters on, but
  // registrants know their jurisdictions by name, not by the numeric code.
  const jurisdictionOptions = React.useMemo(
    () =>
      (jurisdictionOptionsData?.jurisdictions ?? []).map((jurisdiction) => ({
        value: jurisdiction.display_code,
        label: jurisdictionOptionLabel(jurisdiction),
      })),
    [jurisdictionOptionsData?.jurisdictions],
  );

  const registrantOptions = React.useMemo(() => {
    if (!has_multiple_registrant_accounts) return [];
    return (registrantUsersData?.users ?? [])
      .filter((user) => user.status === "active")
      .map((user) => ({
        value: user.id,
        label: user.name?.trim() || user.email,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [has_multiple_registrant_accounts, registrantUsersData?.users]);

  const hasRegistrantUnregisteredProperties =
    (registrantUnregisteredData?.organization_total_count ??
      registrantUnregisteredData?.unfiltered_total_count ??
      registrantUnregisteredData?.total_count ??
      0) > 0;

  const balanceDueCount = statsData?.balance_due_count ?? 0;
  const needsAttentionCount = statsData?.needs_attention_count ?? 0;
  const complianceIssueCount = statsData?.compliance_issue_count ?? 0;
  const bulkPaymentPayLaterCount = statsData?.bulk_payment_count ?? 0;
  const inspectionPendingCount = statsData?.inspection_pending_count ?? 0;

  const licenseTypeOptions = React.useMemo(() => {
    const types = licenseTypesData?.license_types ?? [];
    const names = new Set(licenseTypeNameValues?.values ?? []);
    const matching = names.size
      ? types.filter((type) => names.has(type.name))
      : types;
    return matching
      .map((type) => ({ value: type.id, label: type.name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [licenseTypesData?.license_types, licenseTypeNameValues?.values]);

  const shouldShowLicenseTypeFilter = licenseTypeOptions.length > 1;

  const jurisdictionCatalog = React.useMemo(
    () =>
      (jurisdictionOptionsData?.jurisdictions ?? []).map((jurisdiction) => ({
        id: jurisdiction.display_code,
        name: jurisdiction.name,
      })),
    [jurisdictionOptionsData?.jurisdictions],
  );
  const licenseTypeCatalog = React.useMemo(
    () =>
      licenseTypeOptions.map((option) => ({
        id: option.value,
        name: option.label,
      })),
    [licenseTypeOptions],
  );
  const registrantJurisdictionBuckets = React.useMemo(
    () => [
      {
        columnId: "jurisdiction_name",
        filters: bulkColumnFilters,
        setFilters: setBulkColumnFilters,
      },
      {
        columnId: "jurisdiction_name",
        filters: unregisteredColumnFilters,
        setFilters: setUnregisteredColumnFilters,
      },
    ],
    [bulkColumnFilters, setBulkColumnFilters, unregisteredColumnFilters],
  );
  const registrantLicenseTypeBuckets = React.useMemo(
    () => [
      {
        columnId: "license_type",
        filters: columnFilters,
        setFilters: setColumnFilters,
      },
      {
        columnId: "license_type_name",
        filters: bulkColumnFilters,
        setFilters: setBulkColumnFilters,
      },
      {
        columnId: "license_type_name",
        filters: unregisteredColumnFilters,
        setFilters: setUnregisteredColumnFilters,
      },
    ],
    [
      bulkColumnFilters,
      columnFilters,
      setBulkColumnFilters,
      setColumnFilters,
      unregisteredColumnFilters,
    ],
  );
  useSyncGlobalColumnFilters({
    selectedIds: selectedJurisdictions,
    setSelectedIds: setSelectedJurisdictions,
    options: jurisdictionCatalog,
    buckets: registrantJurisdictionBuckets,
  });
  useSyncGlobalColumnFilters({
    selectedIds: selectedLicenseTypes,
    setSelectedIds: setSelectedLicenseTypes,
    options: licenseTypeCatalog,
    buckets: registrantLicenseTypeBuckets,
  });

  React.useEffect(() => {
    if (!jurisdictionOptionsData) return;
    const allowed = new Set(jurisdictionOptions.map((option) => option.value));
    const next = selectedJurisdictions.filter((code) => allowed.has(code));
    if (next.length !== selectedJurisdictions.length) {
      setSelectedJurisdictions(next);
    }
  }, [
    jurisdictionOptionsData,
    jurisdictionOptions,
    selectedJurisdictions,
    setSelectedJurisdictions,
  ]);

  React.useEffect(() => {
    if (!licenseTypesData) return;
    const allowed = new Set(licenseTypeOptions.map((option) => option.value));
    const next = selectedLicenseTypes.filter((id) => allowed.has(id));
    if (next.length !== selectedLicenseTypes.length) {
      setSelectedLicenseTypes(next);
    }
  }, [
    licenseTypesData,
    licenseTypeOptions,
    selectedLicenseTypes,
    setSelectedLicenseTypes,
  ]);

  React.useEffect(() => {
    if (!selectedRegistrantAccountId || !registrantUsersData) return;
    const allowed = new Set(registrantOptions.map((option) => option.value));
    if (!allowed.has(selectedRegistrantAccountId)) {
      setSelectedRegistrantAccountId(null);
    }
  }, [
    registrantUsersData,
    registrantOptions,
    selectedRegistrantAccountId,
    setSelectedRegistrantAccountId,
  ]);

  const questionColumns = React.useMemo(
    () => listableRegistrantQuestionColumns(rawQuestionColumns),
    [rawQuestionColumns],
  );

  const cardFacetedValues = React.useMemo(
    () => computeRegistrantLicenseFacetedValues(licenses, questionColumns),
    [licenses, questionColumns],
  );

  const bulkAccessorKeys = React.useMemo(
    () =>
      buildRegistrantBulkAccessorKeys(
        has_multiple_registrant_accounts,
        questionColumns,
      ),
    [has_multiple_registrant_accounts, questionColumns],
  );

  const bulkGetColumnProperties = React.useMemo(
    () =>
      buildRegistrantBulkGetColumnProperties(
        facetValuesByColumn,
        { inactiveTab: selectedTab === "inactive" },
        questionColumns,
      ),
    [selectedTab, questionColumns, facetValuesByColumn],
  );

  const cardMapAccessorKeys = React.useMemo(() => {
    const base = REGISTRANT_LICENSE_COLUMN_KEYS.filter(
      (key) => key !== "registrant" || has_multiple_registrant_accounts,
    );
    return [
      ...base,
      ...questionColumns.map((column) =>
        registrantQuestionColumnAccessorKey(column.key),
      ),
    ];
  }, [has_multiple_registrant_accounts, questionColumns]);

  const cardMapFacetValues = React.useMemo(() => {
    const merged = { ...cardFacetedValues };
    for (const [key, values] of Object.entries(facetValuesByColumn)) {
      if (values.length > 0) merged[key] = values;
    }
    return merged;
  }, [cardFacetedValues, facetValuesByColumn]);

  const cardMapGetColumnProperties = React.useMemo(
    () =>
      buildRegistrantCardMapColumnProperties(
        cardMapFacetValues,
        questionColumns,
      ),
    [cardMapFacetValues, questionColumns],
  );

  const columnFilterBadges = React.useMemo(() => {
    if (registrantBulkMode) {
      return buildColumnFilterBadges(
        bulkColumnFilters,
        bulkAccessorKeys,
        bulkGetColumnProperties,
      );
    }
    return buildRegistrantColumnFilterBadges(columnFilters);
  }, [
    registrantBulkMode,
    bulkColumnFilters,
    bulkAccessorKeys,
    bulkGetColumnProperties,
    columnFilters,
  ]);

  const handleClearColumnFilters = React.useCallback(() => {
    if (registrantBulkMode) {
      setBulkColumnFilters({});
      return;
    }
    setColumnFilters({});
  }, [registrantBulkMode, setBulkColumnFilters, setColumnFilters]);

  const handleRemoveColumnFilter = React.useCallback(
    (badge: ColumnFilterBadge) => {
      if (registrantBulkMode) {
        setBulkColumnFilters((current) =>
          removeColumnFilterBadge(current, badge),
        );
        return;
      }
      setColumnFilters((current) => removeColumnFilterBadge(current, badge));
    },
    [registrantBulkMode, setBulkColumnFilters, setColumnFilters],
  );

  const handlePinClick = React.useCallback((licenseId: string) => {
    setSelectedLicenseId(licenseId);
    setIsSheetOpen(true);
  }, []);

  const handleInactiveListLicenseRowClick = React.useCallback(
    (licenseId: string) => {
      setSelectedLicenseId(licenseId);
      setIsSheetOpen(true);
    },
    [],
  );

  const handleDetailsSheetOpenChange = React.useCallback((open: boolean) => {
    setIsSheetOpen(open);
    if (!open) {
      setSelectedLicenseId(null);
      setLicenseDetailsInitialFocus(null);
    }
  }, []);

  const handleLicenseDetailsInitialFocusConsumed = React.useCallback(() => {
    setLicenseDetailsInitialFocus(null);
  }, []);

  React.useEffect(() => {
    if (
      search.tab === "needs-attention" ||
      search.tab === "compliance-issues"
    ) {
      setSelectedTab(search.tab);
    }
    if (
      !search.openLicense &&
      search.tab !== "needs-attention" &&
      search.tab !== "compliance-issues"
    )
      return;
    if (search.openLicense) {
      setSelectedLicenseId(search.openLicense);
      setIsSheetOpen(true);
      if (search.focusProperty) {
        setLicenseDetailsInitialFocus("property");
      }
    }
    void navigate({
      to: "/registrant/dashboard",
      search: {},
      replace: true,
    });
  }, [
    search.openLicense,
    search.focusProperty,
    search.tab,
    navigate,
    setSelectedTab,
  ]);

  React.useEffect(() => {
    setLicenseDisplayedSnapshot({
      openLicenseId: isSheetOpen ? selectedLicenseId : null,
    });
  }, [isSheetOpen, selectedLicenseId]);

  const isLicenseWorkspaceTab = LICENSE_WORKSPACE_TABS.has(selectedTab);
  const [licenseWorkspaceMounted, setLicenseWorkspaceMounted] = React.useState(
    () => isLicenseWorkspaceTab,
  );
  React.useEffect(() => {
    if (isLicenseWorkspaceTab) {
      setLicenseWorkspaceMounted(true);
    }
  }, [isLicenseWorkspaceTab]);

  const handleWorkspaceTabChange = React.useCallback(
    (tab: string) => {
      setSelectedTab(tab);
      if (tab !== "all") {
        setBulkPaymentPayLaterCohortFilterActive(false);
      }
      if (tab !== "all") {
        setBalanceDueCohortFilterActive(false);
      }
    },
    [
      setSelectedTab,
      setBalanceDueCohortFilterActive,
      setBulkPaymentPayLaterCohortFilterActive,
    ],
  );

  const handleViewChange = React.useCallback(
    (nextIsMapView: boolean) => {
      if (!nextIsMapView && isPinsFetching) {
        void cancelPinsLoad();
      }
      setIsMapView(nextIsMapView);
    },
    [cancelPinsLoad, isPinsFetching, setIsMapView],
  );

  const handleViewBalanceDueRegistrations = React.useCallback(() => {
    setSelectedTab("balance-due");
    setIsMapView(false);
    setBalanceDueCohortFilterActive(false);
    setBulkPaymentPayLaterCohortFilterActive(false);
    setColumnFilters({});
    setBulkColumnFilters({});
  }, [
    setSelectedTab,
    setIsMapView,
    setBalanceDueCohortFilterActive,
    setBulkPaymentPayLaterCohortFilterActive,
    setColumnFilters,
    setBulkColumnFilters,
  ]);

  const handleViewNeedsAttentionRegistrations = React.useCallback(() => {
    setSelectedTab("needs-attention");
    setIsMapView(false);
    setBalanceDueCohortFilterActive(false);
    setBulkPaymentPayLaterCohortFilterActive(false);
    setColumnFilters({});
    setBulkColumnFilters({});
  }, [
    setSelectedTab,
    setIsMapView,
    setBalanceDueCohortFilterActive,
    setBulkPaymentPayLaterCohortFilterActive,
    setColumnFilters,
    setBulkColumnFilters,
  ]);

  const handleViewComplianceIssueRegistrations = React.useCallback(() => {
    setSelectedTab("compliance-issues");
    setIsMapView(false);
    setBalanceDueCohortFilterActive(false);
    setBulkPaymentPayLaterCohortFilterActive(false);
    setColumnFilters({});
    setBulkColumnFilters({});
  }, [
    setSelectedTab,
    setIsMapView,
    setBalanceDueCohortFilterActive,
    setBulkPaymentPayLaterCohortFilterActive,
    setColumnFilters,
    setBulkColumnFilters,
  ]);

  const handleViewInspectionPendingRegistrations = React.useCallback(() => {
    setSelectedTab("all");
    setIsMapView(false);
    setBalanceDueCohortFilterActive(false);
    setBulkPaymentPayLaterCohortFilterActive(false);
    setColumnFilters({ status: ["Inspection Pending"] });
    setBulkColumnFilters({});
  }, [
    setSelectedTab,
    setIsMapView,
    setBalanceDueCohortFilterActive,
    setBulkPaymentPayLaterCohortFilterActive,
    setColumnFilters,
    setBulkColumnFilters,
  ]);

  const handleViewBulkPaymentPayLaterRegistrations = React.useCallback(() => {
    setSelectedTab("all");
    setIsMapView(false);
    setBalanceDueCohortFilterActive(false);
    setBulkPaymentPayLaterCohortFilterActive(true);
    setBulkColumnFilters({});
    setColumnFilters({});
  }, [
    setSelectedTab,
    setIsMapView,
    setBalanceDueCohortFilterActive,
    setBulkPaymentPayLaterCohortFilterActive,
    setBulkColumnFilters,
    setColumnFilters,
  ]);

  const clearBalanceDueCohortFilter = React.useCallback(() => {
    setBalanceDueCohortFilterActive(false);
  }, [setBalanceDueCohortFilterActive]);

  const clearBulkPaymentPayLaterCohortFilter = React.useCallback(() => {
    setBulkPaymentPayLaterCohortFilterActive(false);
    setColumnFilters({});
  }, [setBulkPaymentPayLaterCohortFilterActive, setColumnFilters]);

  const handleRefresh = React.useCallback(async () => {
    setIsListRefreshDialogOpen(true);
    try {
      await refreshLicensesAfterLocalChange(queryClient, []);
    } finally {
      setIsListRefreshDialogOpen(false);
    }
  }, [queryClient]);

  const filterValuesRequestByColumnRef = React.useRef<Record<string, number>>(
    {},
  );
  // Keep the latest list-query inputs in a ref so opening/refetching facet
  // values does not recreate this callback when filters change — that used to
  // retrigger the popover effect and remount Radix Presence (#185).
  const filterPopoverQueryRef = React.useRef({
    selectedJurisdictions,
    selectedLicenseTypes,
    listColumnFilters,
    listTab,
    bulkPaymentPayLaterCohortFilterActive,
    selectedRegistrantAccountId,
    selectedAms,
    amsQuestionId,
  });
  filterPopoverQueryRef.current = {
    selectedJurisdictions,
    selectedLicenseTypes,
    listColumnFilters,
    listTab,
    bulkPaymentPayLaterCohortFilterActive,
    selectedRegistrantAccountId,
    selectedAms,
    amsQuestionId,
  };
  const handleFilterPopoverOpen = React.useCallback(
    async (columnId: string, search?: string) => {
      const requestId =
        (filterValuesRequestByColumnRef.current[columnId] ?? 0) + 1;
      filterValuesRequestByColumnRef.current[columnId] = requestId;
      const q = filterPopoverQueryRef.current;
      try {
        const values = await fetchRegistrantLicenseFilterValues({
          column: columnId,
          jurisdictionDisplayCodes: q.selectedJurisdictions,
          licenseTypeIds: q.selectedLicenseTypes,
          columnFilters: q.listColumnFilters,
          tab: q.listTab,
          bulkPaymentCohort: q.bulkPaymentPayLaterCohortFilterActive,
          registrantAccountId: q.selectedRegistrantAccountId,
          amsQuestionId: q.selectedAms ? q.amsQuestionId : undefined,
          amsCompanyName: q.selectedAms,
          search,
        });
        if (filterValuesRequestByColumnRef.current[columnId] !== requestId) {
          return;
        }
        setFacetValuesByColumn((prev) => {
          const existing = prev[columnId];
          if (
            existing &&
            existing.length === values.length &&
            existing.every((value, index) => value === values[index])
          ) {
            return prev;
          }
          return { ...prev, [columnId]: values };
        });
      } catch {
        if (filterValuesRequestByColumnRef.current[columnId] !== requestId) {
          return;
        }
        setFacetValuesByColumn((prev) => {
          if (prev[columnId]?.length === 0) return prev;
          return { ...prev, [columnId]: [] };
        });
      }
    },
    [],
  );

  const handleServerExport = React.useCallback(
    async (mode: ExportColumnMode, format: "csv" | "excel") => {
      const rows = await fetchRegistrantLicenseExport({
        jurisdictionDisplayCodes: selectedJurisdictions,
        licenseTypeIds: selectedLicenseTypes,
        columnFilters: listColumnFilters,
        sortBy,
        sortDir,
        tab: listTab,
        bulkPaymentCohort: bulkPaymentPayLaterCohortFilterActive,
        registrantAccountId: selectedRegistrantAccountId,
        amsQuestionId: selectedAms ? amsQuestionId : undefined,
        amsCompanyName: selectedAms,
      });
      const exportKeys = getExportAccessorKeysForMode(
        mode,
        null,
        bulkAccessorKeys,
        undefined,
        bulkColumnVisibility,
      );
      if (format === "csv") {
        exportTableRowsToCsv({
          rows,
          exportKeys,
          getColumnProperties: bulkGetColumnProperties,
          fileBasename: "registrations",
        });
      } else {
        exportTableRowsToExcel({
          rows,
          exportKeys,
          getColumnProperties: bulkGetColumnProperties,
          fileBasename: "registrations",
        });
      }
    },
    [
      selectedJurisdictions,
      selectedLicenseTypes,
      listColumnFilters,
      sortBy,
      sortDir,
      listTab,
      bulkPaymentPayLaterCohortFilterActive,
      selectedRegistrantAccountId,
      selectedAms,
      amsQuestionId,
      bulkAccessorKeys,
      bulkColumnVisibility,
      bulkGetColumnProperties,
    ],
  );

  const universeStatsReady = universeStats != null;
  const inactiveProbeReady =
    (universeStats?.total_registrations ?? 0) > 0 || inactiveProbe != null;
  const hasAnyLicenses =
    totalCount > 0 ||
    licenses.length > 0 ||
    (universeStats?.total_registrations ?? 0) > 0 ||
    (inactiveProbe?.total_count ?? 0) > 0;

  if (!isAuthSetupComplete) {
    return <LoadingScreen message="Authenticating..." />;
  }
  if (isQueryLoading && licenses.length === 0) {
    return <LoadingScreen message="Fetching data..." />;
  }
  if (queryError) {
    return (
      <ErrorComponent
        title={getQueryErrorHeading(queryError)}
        message={getQueryErrorMessage(queryError)}
      />
    );
  }

  if (
    !hasAnyLicenses &&
    (!universeStatsReady ||
      !inactiveProbeReady ||
      isRegistrantUnregisteredLoading) &&
    selectedTab !== "payments" &&
    selectedTab !== "invoices" &&
    selectedTab !== "users" &&
    selectedTab !== "inspections"
  ) {
    return <LoadingScreen message="Fetching data..." />;
  }

  if (
    !hasAnyLicenses &&
    !hasRegistrantUnregisteredProperties &&
    selectedTab !== "payments" &&
    selectedTab !== "invoices" &&
    selectedTab !== "users" &&
    selectedTab !== "unregistered-properties" &&
    selectedTab !== "inspections"
  ) {
    return <EmptyDashboard />;
  }

  const renderDashboardHeader = (stickyTitle = false) => (
    <DashboardHeader
      stickyTitle={stickyTitle}
      helpArticleId={getDashboardContextHelpArticleId(selectedTab)}
      selectedJurisdictions={selectedJurisdictions}
      onSelectedJurisdictionsChange={setSelectedJurisdictions}
      jurisdictionOptions={jurisdictionOptions}
      selectedLicenseTypes={selectedLicenseTypes}
      onSelectedLicenseTypesChange={setSelectedLicenseTypes}
      licenseTypeOptions={licenseTypeOptions}
      shouldShowLicenseTypeFilter={shouldShowLicenseTypeFilter}
      hideBalanceDue
      balanceDueCount={balanceDueCount}
      needsAttentionCount={needsAttentionCount}
      complianceIssueCount={complianceIssueCount}
      onViewComplianceIssueRegistrations={
        handleViewComplianceIssueRegistrations
      }
      bulkPaymentPayLaterCount={bulkPaymentPayLaterCount}
      inspectionPendingCount={inspectionPendingCount}
      onViewInspectionPendingRegistrations={
        handleViewInspectionPendingRegistrations
      }
      onSelectWorkspaceTab={handleWorkspaceTabChange}
      onViewBalanceDueRegistrations={handleViewBalanceDueRegistrations}
      onViewNeedsAttentionRegistrations={handleViewNeedsAttentionRegistrations}
      onViewBulkPaymentPayLaterRegistrations={
        handleViewBulkPaymentPayLaterRegistrations
      }
      registrantBalanceDueCohortActive={false}
      onClearRegistrantBalanceDueCohort={clearBalanceDueCohortFilter}
      registrantBulkPaymentPayLaterCohortActive={
        bulkPaymentPayLaterCohortFilterActive
      }
      onClearRegistrantBulkPaymentPayLaterCohort={
        clearBulkPaymentPayLaterCohortFilter
      }
      hasMultipleRegistrantAccounts={has_multiple_registrant_accounts}
      registrantOptions={registrantOptions}
      selectedRegistrantAccountId={selectedRegistrantAccountId}
      onSelectedRegistrantAccountIdChange={setSelectedRegistrantAccountId}
      columnFilters={omitGloballyLinkedColumnFilters(columnFilters)}
      onColumnFiltersChange={setColumnFilters}
      selectedAms={selectedAms}
      onSelectedAmsChange={setSelectedAms}
      amsOptions={amsOptions}
    />
  );

  const dashboardTabsBar = (
    <div
      data-slot="registrant-dashboard-tabs"
      className="flex w-full min-w-0 flex-wrap items-center justify-between gap-2"
    >
      <div className="min-w-0 max-w-full flex-1 overflow-x-auto">
        <DashboardTabList
          showUsersTab={isAdmin}
          showComplianceIssuesTab
          needsAttentionCount={needsAttentionCount}
          complianceIssueCount={complianceIssueCount}
        />
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {selectedTab !== "users" &&
          selectedTab !== "payments" &&
          selectedTab !== "invoices" &&
          selectedTab !== "unregistered-properties" &&
          selectedTab !== "inspections" &&
          !isMapView &&
          !registrantBulkMode && (
            <>
              <Button
                variant="outline"
                size="icon"
                aria-label="Refresh registrations"
                title="Refresh registrations"
                onClick={() => void handleRefresh()}
                disabled={isListFetching || isListRefreshDialogOpen}
              >
                <RefreshCw
                  className={cn(
                    "h-4 w-4",
                    (isListFetching || isListRefreshDialogOpen) &&
                      "animate-spin",
                  )}
                />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline">
                    <Columns3 className="mr-2 h-4 w-4" />
                    Columns <ChevronDown className="ml-2 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {REGISTRANT_LICENSE_COLUMN_KEYS.filter(
                    (key) =>
                      key !== "registrant" || has_multiple_registrant_accounts,
                  ).map((key) => (
                    <DropdownMenuCheckboxItem
                      key={key}
                      className="capitalize"
                      checked={columnVisibility[key] !== false}
                      onCheckedChange={(checked) =>
                        setColumnVisibility((prev) => ({
                          ...prev,
                          [key]: !!checked,
                        }))
                      }
                    >
                      {getRegistrantLicenseColumnDisplayName(key)}
                    </DropdownMenuCheckboxItem>
                  ))}
                  {questionColumns.map((column) => {
                    const accessorKey = registrantQuestionColumnAccessorKey(
                      column.key,
                    );
                    return (
                      <DropdownMenuCheckboxItem
                        key={accessorKey}
                        checked={
                          columnVisibility[accessorKey] ??
                          column.default_visible === true
                        }
                        onCheckedChange={(checked) =>
                          setColumnVisibility((prev) => ({
                            ...prev,
                            [accessorKey]: !!checked,
                          }))
                        }
                      >
                        {column.label}
                      </DropdownMenuCheckboxItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        {selectedTab !== "users" &&
          selectedTab !== "payments" &&
          selectedTab !== "invoices" &&
          selectedTab !== "unregistered-properties" &&
          selectedTab !== "inspections" && (
            <>
              {isMapView ? (
                <Button
                  variant="outline"
                  size="icon"
                  aria-label="Refresh registrations"
                  title="Refresh registrations"
                  onClick={() => void handleRefresh()}
                  disabled={
                    isListFetching || isPinsFetching || isListRefreshDialogOpen
                  }
                >
                  <RefreshCw
                    className={cn(
                      "h-4 w-4",
                      (isListFetching ||
                        isPinsFetching ||
                        isListRefreshDialogOpen) &&
                        "animate-spin",
                    )}
                  />
                </Button>
              ) : null}
              <DashboardViewToggle
                isMapView={isMapView}
                onViewChange={handleViewChange}
              />
            </>
          )}
      </div>
    </div>
  );

  // Every tab renders the title, filters and tab strip inside its own list
  // scroller so the rows can run to the top of the screen as the user scrolls.
  const listHostsPageChrome = true;

  const renderPageChrome = (trailing?: React.ReactNode) => (
    <div className="pb-4">
      {renderDashboardHeader(true)}
      {dashboardTabsBar}
      {trailing}
    </div>
  );

  return (
    <div className="flex flex-1 min-h-0 w-full min-w-[375px] flex-col gap-6 overflow-hidden">
      <Tabs
        value={selectedTab}
        onValueChange={handleWorkspaceTabChange}
        className="flex min-h-0 flex-1 flex-col min-w-0"
      >
        {listHostsPageChrome ? null : (
          <div className="space-y-8">
            {renderDashboardHeader()}
            {dashboardTabsBar}
          </div>
        )}

        <TabsContent
          value="users"
          className="mt-0 flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden"
        >
          <DashboardUsers
            isAuthSetupComplete={isAuthSetupComplete}
            leadingContent={renderPageChrome()}
          />
        </TabsContent>
        <TabsContent
          value="inspections"
          className="mt-0 flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden"
        >
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
            {renderPageChrome()}
            <RegistrantInspectionDispatchPage embedded />
          </div>
        </TabsContent>
        <TabsContent
          value="invoices"
          className="mt-0 flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden"
        >
          <DashboardInvoices
            isAuthSetupComplete={isAuthSetupComplete}
            selectedAms={selectedAms}
            leadingContent={renderPageChrome()}
          />
        </TabsContent>
        <TabsContent
          value="payments"
          className="mt-0 flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden"
        >
          <DashboardPayments
            isAuthSetupComplete={isAuthSetupComplete}
            selectedAms={selectedAms}
            leadingContent={renderPageChrome()}
          />
        </TabsContent>
        <TabsContent
          value="unregistered-properties"
          className="mt-0 flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden"
        >
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
            {renderPageChrome()}
            {isRegistrantUnregisteredLoading ? (
              <LoadingScreen message="Loading unregistered properties…" />
            ) : registrantUnregisteredError ? (
              <ErrorComponent
                title={getQueryErrorHeading(registrantUnregisteredError)}
                message={getQueryErrorMessage(registrantUnregisteredError)}
              />
            ) : registrantUnregisteredData ? (
              <DashboardRegistrantUnregistered
                data={registrantUnregisteredData}
                showRegistrantColumn={
                  registrantUnregisteredData.has_multiple_registrant_accounts ??
                  has_multiple_registrant_accounts
                }
                columnFilters={unregisteredColumnFilters}
                onColumnFiltersChange={setUnregisteredColumnFilters}
                columnVisibility={unregisteredColumnVisibility}
                onColumnVisibilityChange={setUnregisteredColumnVisibility}
                pageIndex={unregisteredPageIndex}
                pageSize={unregisteredPageSize}
                onPaginationChange={({ pageIndex, pageSize }) => {
                  setUnregisteredPageIndex(pageIndex);
                  setUnregisteredPageSize(pageSize);
                }}
                sorting={unregisteredSorting}
                onSortingChange={setUnregisteredSorting}
                isRefreshing={isRegistrantUnregisteredFetching}
              />
            ) : null}
          </div>
        </TabsContent>

        {licenseWorkspaceMounted ? (
          <>
            <div
              className={cn(
                "flex min-h-0 flex-1 flex-col min-w-0 overflow-hidden",
                (!isLicenseWorkspaceTab || isMapView) && "hidden",
              )}
              aria-hidden={!isLicenseWorkspaceTab || isMapView}
            >
              <DashboardList
                leadingContent={renderPageChrome()}
                bulkPaymentPayLaterCohortOnly={
                  selectedTab === "all" && bulkPaymentPayLaterCohortFilterActive
                }
                allActiveRegistrationsJurisdictions={
                  allActiveRegistrationsJurisdictions
                }
                needsAttentionLicenses={needsAttentionLicenses}
                complianceIssueLicenses={complianceIssueLicenses}
                balanceDueLicenses={balanceDueLicenses}
                renewableLicenses={renewableLicenses}
                inactiveLicenses={inactiveLicenses}
                userType={UserType.REGISTRANT}
                showRegistrantColumn={has_multiple_registrant_accounts}
                columnVisibility={columnVisibility}
                columnFilters={columnFilters}
                onColumnFiltersChange={setColumnFilters}
                columnFilterBadges={columnFilterBadges}
                onClearColumnFilters={handleClearColumnFilters}
                onRemoveColumnFilter={handleRemoveColumnFilter}
                facetedValuesByColumn={
                  registrantBulkMode ? facetValuesByColumn : cardFacetedValues
                }
                onInactiveLicenseRowClick={handleInactiveListLicenseRowClick}
                questionColumns={questionColumns}
                bulkColumnFilters={bulkColumnFilters}
                onBulkColumnFiltersChange={setBulkColumnFilters}
                bulkColumnVisibility={bulkColumnVisibility}
                onBulkColumnVisibilityChange={setBulkColumnVisibility}
                onBulkLicenseRowClick={handleInactiveListLicenseRowClick}
                listTotalCount={totalCount}
                bulkLayoutTotalCount={bulkLayoutTotalCount}
                serverMode
                serverRowOrder={grouped.serverRowOrder}
                pageIndex={pageIndex}
                pageSize={pageSize}
                onPaginationChange={(next) => {
                  setPageIndex(next.pageIndex);
                  setPageSize(next.pageSize);
                }}
                sorting={sorting}
                onSortingChange={setSorting}
                onRefresh={handleRefresh}
                isRefreshing={
                  isListForegroundFetching ||
                  (isMapView && isPinsForegroundFetching) ||
                  isListRefreshDialogOpen
                }
                dataUpdatedAt={dataUpdatedAt}
                onFilterPopoverOpen={handleFilterPopoverOpen}
                onServerExport={handleServerExport}
              />
            </div>
            {isLicenseWorkspaceTab && isMapView ? (
              <div className="flex min-h-0 flex-1 flex-col min-w-0 overflow-hidden">
                {isPinsForegroundFetching ? (
                  <div className="pointer-events-none fixed inset-0 z-[9000] flex items-center justify-center">
                    <dialog
                      open
                      aria-modal="false"
                      aria-labelledby="map-properties-loading-title"
                      className="flex items-center gap-3 rounded-lg border bg-background px-6 py-4 shadow-lg"
                    >
                      <Loader2
                        className="h-5 w-5 animate-spin"
                        aria-hidden="true"
                      />
                      <span
                        id="map-properties-loading-title"
                        className="font-medium"
                      >
                        Loading Properties
                      </span>
                    </dialog>
                  </div>
                ) : null}
                <DashboardMapViewLayout
                  leadingContent={renderPageChrome()}
                  facets={
                    <DashboardMapFacetRail
                      data={licenses}
                      accessorKeys={
                        registrantBulkMode
                          ? bulkAccessorKeys
                          : cardMapAccessorKeys
                      }
                      getColumnProperties={
                        registrantBulkMode
                          ? bulkGetColumnProperties
                          : cardMapGetColumnProperties
                      }
                      columnFilters={listColumnFilters}
                      onColumnFiltersChange={
                        registrantBulkMode
                          ? setBulkColumnFilters
                          : setColumnFilters
                      }
                      onFilterValuesRequest={handleFilterPopoverOpen}
                      tablePersistenceKey={
                        registrantBulkMode
                          ? has_multiple_registrant_accounts
                            ? "registrant-bulk-registrations-with-registrant"
                            : "registrant-bulk-registrations"
                          : undefined
                      }
                    />
                  }
                  filterBadges=<DashboardColumnFilterBadges
                    badges={columnFilterBadges}
                    onClearAll={handleClearColumnFilters}
                    onRemove={handleRemoveColumnFilter}
                  />
                >
                  <MapErrorBoundary
                    onSwitchToListView={() => handleViewChange(false)}
                    className="h-full w-full"
                  >
                    <DashboardMap
                      pins={pins}
                      clusters={mapClusters}
                      totalInView={mapTotalInView}
                      isLoading={isPinsFetching}
                      onViewportChange={handleMapViewport}
                      onPinClick={handlePinClick}
                      className="h-full min-h-0 w-full"
                      showRegistrationStatusFlowInfo={false}
                    />
                  </MapErrorBoundary>
                </DashboardMapViewLayout>
              </div>
            ) : null}
          </>
        ) : null}
      </Tabs>

      <Dialog open={isListRefreshDialogOpen}>
        <DialogContent
          showCloseButton={false}
          onEscapeKeyDown={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          aria-describedby="registrant-list-refresh-description"
        >
          <DialogHeader>
            <DialogTitle>Refreshing registrations</DialogTitle>
            <DialogDescription id="registrant-list-refresh-description">
              Please wait while we update your registration list.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-center py-2">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </DialogContent>
      </Dialog>

      <LicenseDetailsSheet
        isOpen={isSheetOpen}
        onOpenChange={handleDetailsSheetOpenChange}
        isSelectedLicenseLoading={isSelectedLicenseLoading}
        isAdditionalQuestionsQueryLoading={isAdditionalQuestionsQueryLoading}
        selectedLicenseError={selectedLicenseError}
        additionalQuestionsQueryError={additionalQuestionsQueryError}
        additionalQuestions={additionalQuestions}
        selectedLicenseData={selectedLicenseData}
        commentsData={commentsData}
        isCommentsQueryLoading={isCommentsQueryLoading}
        commentsQueryError={commentsQueryError}
        paymentsData={paymentsData}
        isPaymentsQueryLoading={isPaymentsQueryLoading}
        paymentsQueryError={paymentsQueryError}
        isLicenseRuntimeRefreshing={isSelectedLicenseRuntimeRefreshing}
        initialFocusSection={licenseDetailsInitialFocus}
        onInitialFocusSectionConsumed={handleLicenseDetailsInitialFocusConsumed}
        userType={UserType.REGISTRANT}
        key={selectedLicenseId}
      />
    </div>
  );
}
